
import React from 'react';
import { Check, Loader2 } from 'lucide-react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { CardContent } from '@/components/ui/card';
import RemediationStatusBadges from './RemediationStatusBadges';
import RemediationActions from './RemediationActions';

interface Paper {
  id: string;
  title: string;
  abstract: string;
  file_url: string;
  uploaded_at: string;
  keywords?: string[];
  needs_ai_review?: boolean;
  needs_keywords?: boolean;
  needs_research_info?: boolean;
}

interface RemediationTableProps {
  papers: Paper[];
  loading: boolean;
  processingPapers: Set<string>;
  processingKeywords: Set<string>;
  processingResearchInfo: Set<string>;
  deletingPapers: Set<string>;
  onGenerateAIReview: (paper: Paper) => void;
  onGenerateKeywords: (paper: Paper) => void;
  onGenerateResearchInfo: (paper: Paper) => void;
  onDeletePaper: (paper: Paper) => void;
}

const RemediationTable: React.FC<RemediationTableProps> = ({
  papers,
  loading,
  processingPapers,
  processingKeywords,
  processingResearchInfo,
  deletingPapers,
  onGenerateAIReview,
  onGenerateKeywords,
  onGenerateResearchInfo,
  onDeletePaper
}) => {
  return (
    <CardContent>
      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : papers.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          <Check className="h-12 w-12 mx-auto mb-2 text-green-500" />
          <p>No papers currently need remediation!</p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Uploaded</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {papers.map((paper) => (
              <TableRow key={paper.id}>
                <TableCell className="font-medium truncate max-w-[300px]">
                  {paper.title}
                </TableCell>
                <TableCell>
                  <RemediationStatusBadges paper={paper} />
                </TableCell>
                <TableCell>
                  {new Date(paper.uploaded_at).toLocaleDateString()}
                </TableCell>
                <TableCell className="text-right">
                  <RemediationActions
                    paper={paper}
                    processingPapers={processingPapers}
                    processingKeywords={processingKeywords}
                    processingResearchInfo={processingResearchInfo}
                    deletingPapers={deletingPapers}
                    onGenerateAIReview={onGenerateAIReview}
                    onGenerateKeywords={onGenerateKeywords}
                    onGenerateResearchInfo={onGenerateResearchInfo}
                    onDeletePaper={onDeletePaper}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </CardContent>
  );
};

export default RemediationTable;
